import { recursiveFreeze } from 'app/shared/utilities/recursive_freeze.service';

const BITRATE_UNITS: any = {
    values: [
        {
            suffix: 'bit/s',
            multiplier: 1,
            max_value: 1000,
        },
        {
            suffix: 'kbit/s',
            multiplier: 1000,
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'Mbit/s',
            multiplier: 1e6,
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'Gbit/s',
            multiplier: 1e9,
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'Tbit/s',
            multiplier: 1e12,
            min_value: 1,
        },
    ],
};

const BIT_UNITS: any = {
    values: [
        {
            suffix: 'bit',
            multiplier: 1,
            max_value: 1000,
        },
        {
            suffix: 'kbit',
            multiplier: 1000,
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'Mbit',
            multiplier: 1e6,
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'Gbit',
            multiplier: 1e9,
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'Tbit',
            multiplier: 1e12,
            min_value: 1,
        },
    ],
};

const BYTERATE_UNITS: any = {
    values: [
        {
            suffix: 'B/s',
            multiplier: 1,
            max_value: 1000,
        },
        {
            suffix: 'kB/s',
            multiplier: Math.pow(2, 10),
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'MB/s',
            multiplier: Math.pow(2, 20),
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'GB/s',
            multiplier: Math.pow(2, 30),
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'TB/s',
            multiplier: Math.pow(2, 40),
            min_value: 1,
        },
    ],
};

const BYTE_UNITS: any = {
    values: [
        {
            suffix: 'B',
            multiplier: 1,
            max_value: 1000,
        },
        {
            suffix: 'kB',
            multiplier: Math.pow(2, 10),
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'MB',
            multiplier: Math.pow(2, 20),
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'GB',
            multiplier: Math.pow(2, 30),
            min_value: 1,
            max_value: 1000,
        },
        {
            suffix: 'TB',
            multiplier: Math.pow(2, 40),
            min_value: 1,
        },
    ],
};

const TIME_UNITS: any = {
    values: [
        {
            suffix: 'millisecond',
            multiplier: 1,
            max_value: 1000,
        },
        {
            suffix: 'second',
            multiplier: 1000,
            min_value: 1,
            max_value: 60,
        },
        {
            suffix: 'minute',
            multiplier: 60000,
            min_value: 1,
            max_value: 60,
        },
        {
            suffix: 'hour',
            multiplier: 3600000,
            min_value: 1,
            max_value: 24,
        },
        {
            suffix: 'day',
            multiplier: 86400000,
            min_value: 1,
        },
    ],
};

// TODO: this special handling is needed because Safari does not support RegEx lookbehind; ref.: https://caniuse.com/js-regexp-lookbehind
let hostnameRegex;
try {
    // found this regex here https://stackoverflow.com/a/20204811/7884214
    hostnameRegex = eval(
        '/(?=^.{1,253}$)(^(((?!-)[a-zA-Z0-9-]{1,63}(?<!-))|((?!-)[a-zA-Z0-9-]{1,63}(?<!-)\\.)+[a-zA-Z]{2,63})$)/',
    );
} catch (e) {
    hostnameRegex = /.*/;
    console.warn(`Attempt to define hostname RegEx raised ${e}; are you using Safari?`);
}

const constants = {
    CENTER_COLLAPSE_SCREEN_WIDTH: 825,

    Dashboard: {
        gridRowHeight: 54,
    },

    URL: {
        LICENCE_URL: 'https://licensing.ftpsolutions.com.au/auth/',
        FEEDBACK_URL: 'https://licensing.ftpsolutions.com.au/feedback/',
        SIGN_IN_TRACK_URL: 'https://licensing.ftpsolutions.com.au/sign_in/',
        IMAGES_URL: '/assets/images/',
        ICONS_URL: '/assets/icons/',
        MAP_ICONS_URL: '/assets/icons/map/',
    },

    Graphics: {
        NULL_BILLBOARD:
            'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==',
    },

    Measurement: {
        TOTAL_CPU: 'Total CPU',
        TOTAL_RAM: 'Total RAM',
        TOTAL_STORAGE: 'Total Storage',
        PERCENTAGE_UNIT: '%',
        PERCENTAGE_STRING: 'percentage',

        BATTERY_CURRENT: 'Battery Current',
        BATTERY_CURRENT_UNIT: 'A',
        BATTERY_CURRENT_UNIT_LONG_PLURAL: 'amps',
        BATTERY_CURRENT_UNIT_LONG_SINGULAR: 'amp',

        BATTERY_VOLTAGE: 'Battery Voltage',
        BATTERY_VOLTAGE_UNIT: 'V',
        BATTERY_VOLTAGE_UNIT_LONG_PLURAL: 'volts',
        BATTERY_VOLTAGE_UNIT_LONG_SINGULAR: 'volt',

        BATTERY_TEMPERATURE: 'Battery Temperature',
        BATTERY_TEMPERATURE_UNIT: '\xB0C',
        BATTERY_TEMPERATURE_UNIT_LONG: 'celsius',

        PANEL_CURRENT: 'Panel Current',
        PANEL_CURRENT_UNIT: 'A',
        PANEL_CURRENT_UNIT_LONG_PLURAL: 'amps',
        PANEL_CURRENT_UNIT_LONG_SINGULAR: 'amp',

        PANEL_VOLTAGE: 'Battery Current',
        PANEL_VOLTAGE_UNIT: 'V',
        PANEL_VOLTAGE_UNIT_LONG_PLURAL: 'volts',
        PANEL_VOLTAGE_UNIT_LONG_SINGULAR: 'volt',

        INPUT_POWER: 'Input Power',
        INPUT_POWER_UNIT: 'W',
        INPUT_POWER_UNIT_LONG_PLURAL: 'watts',
        INPUT_POWER_UNIT_LONG_SINGULAR: 'wat',

        OUTPUT_POWER: 'Output Power',
        OUTPUT_POWER_UNIT: 'W',
        OUTPUT_POWER_UNIT_LONG_PLURAL: 'watts',
        OUTPUT_POWER_UNIT_LONG_SINGULAR: 'watt',

        LATENCY: 'Latency',
        LATENCY_UNIT: 'ms',
        LATENCY_UNIT_LONG_PLURAL: 'milliseconds',
        LATENCY_UNIT_LONG_SINGULAR: 'millisecond',

        CONNECTIVITY: 'Connectivity',
        CONNECTIVITY_UNIT: '%',
        CONNECTIVITY_UNIT_LONG: 'percent',

        TX_POWER: 'TX Power',
        TX_POWER_UNIT: 'dBm',
        TX_POWER_UNIT_LONG_PLURAL: 'decibel-milliwatts',
        TX_POWER_UNIT_LONG_SINGULAR: 'decibel-milliwatt',

        TX_RATE: 'TX Rate',
        TX_RATE_UNIT: 'Mbit/s',
        TX_RATE_UNIT_LONG_PLURAL: 'megabits per second',
        TX_RATE_UNIT_LONG_SINGULAR: 'megabyte per second',

        AVERAGE_TX: 'Average TX',
        AVERAGE_TX_UNIT: 'kB/s',
        AVERAGE_TX_UNIT_LONG_PLURAL: 'kilobits per second',
        AVERAGE_TX_UNIT_LONG_SINGULAR: 'kilobyte per second',

        RX_POWER: 'RX Power',
        RX_POWER_UNIT: 'dBm',
        RX_POWER_UNIT_LONG_PLURAL: 'decibel-milliwatts',
        RX_POWER_UNIT_LONG_SINGULAR: 'decibel-milliwatt',

        RX_RATE: 'RX Rate',
        RX_RATE_UNIT: 'Mbit/s',
        RX_RATE_UNIT_LONG_PLURAL: 'megabits per second',
        RX_RATE_UNIT_LONG_SINGULAR: 'megabyte per second',

        AVERAGE_RX: 'Average RX',
        AVERAGE_RX_UNIT: 'kB/s',
        AVERAGE_RX_UNIT_LONG_PLURAL: 'kilobits per second',
        AVERAGE_RX_UNIT_LONG_SINGULAR: 'kilobyte per second',

        RSSI: 'RSSI',
        RSSI_UNIT: 'dBm',
        RSSI_UNIT_LONG_PLURAL: 'decibel-milliwatts',
        RSSI_UNIT_LONG_SINGULAR: 'decibel-milliwatt',

        NOISE: 'Noise',
        NOISE_UNIT: 'dBm',
        NOISE_UNIT_LONG_PLURAL: 'decibel-milliwatts',
        NOISE_UNIT_LONG_SINGULAR: 'decibel-milliwatt',

        SNR: 'SNR',
        SNR_UNIT: 'dB',
        SNR_UNIT_LONG_PLURAL: 'decibels',
        SNR_UNIT_LONG_SINGULAR: 'decibels',

        RSRP_UNIT: 'dBm',
        RSRQ_UNIT: 'dBm',
        SINR_UNIT: 'dB',

        CHANNEL_UTILISATION_UNIT: '%',

        CHANNEL_UNIT: '',
    },

    Connections: {
        NULL_CHANNEL_COLOR: '#000000',
    },

    // Used to reference data on an ip device stat
    IPDeviceStatKeys: {
        RSSI: 'activeRadioRSSI',
        LATENCY: 'latency',
        CONNECTIVITY: 'connectivity',
        NOISE: 'activeRadioNoise',
        SNR: 'activeRadioSNR',
        TX_RATE: 'activeRadioTxRate',
        RX_RATE: 'activeRadioRxRate',
        MAC: 'nextHopMac',
        IP_DEVICE_ID: 'ipDeviceId',
    },

    UNITS: {
        BITS: BIT_UNITS,
        BITRATE: BITRATE_UNITS,
        BYTES: BYTE_UNITS,
        BYTERATE: BYTERATE_UNITS,
        TIME: TIME_UNITS,
    },

    // Used to reference data on an visible radio stat
    VisibleRadioStatKeys: {
        RSSI: 'rssi',
        IS_NEXT_HOP: 'isNextHop',
        MAC: 'mac',
        COST: 'cost',
        TX_RATE: 'txRate',
    },

    // Used to reference data on an visible radio stat
    SolarStatKeys: {
        BATTERY_VOLTAGE: 'batteryVoltage',
    },

    ImsImageryHeightGroupIndexes: {
        RF_PLAN: 1000,
        HEAT_MAP: 990,
        BASE: 0,
    },

    // Filters out views, systems, integrations, and triggers with the specified keyword in the name
    // Shows the filtered items for the specified username
    FilterOutInternals: {
        FILTER_KEYWORD: 'otsc_internal',
        SHOW_ALL_FOR_USERNAME: 'ftp',
    },

    IPv4Regex:
        /^(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])$/,

    EmailRegex:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,

    NumericRegex: /^-?\d+(\.\d+)?$/,

    HexRegex: /^[a-fA-F0-9]+$/,

    SpecialCharRegex: /[!@#$%^&*()_+=\[\]{};':"\\|,<>\/]+/,

    HostnameRegex: hostnameRegex,

    // checks that has format `ldap://hostname:port` or `ldap://ipAddress:port` used this for help : https://stackoverflow.com/a/20204811/7884214
    LdapAddressRegex:
        /ldaps?:\/\/(?:\b(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\.(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\.(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\.(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\b|\b(?:(?:[a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*(?:[A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])\b):(6553[0-5]|655[0-2][0-9]\d|65[0-4](\d){2}|6[0-4](\d){3}|[1-5](\d){4}|[1-9](\d){0,3})$/,

    DistinguishedNamesRegex: /^((CN=([^,]*)),)?((((?:CN|OU)=[^,]+,?)+),)?((DC=[^,]+,?)+)$/,

    //checks if string is in format long,lat. Got it from: https://www.regextester.com/112728 but swapped lat and long and edited a little
    LongAndLatRegex:
        /^([-+]?((1[0-7][0-9]|[0-9][0-9]|[0-9])(\.\d+)?|180((.[0]+)?))[,])+([-+]?(([1-8][0-9]|[0-9])(\.\d+)?|90(.[0]+)?))$/,

    TimeRegex: /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/,

    CensoredSystemTypeDefault: 'Manual',
};

// Dummy source dynamic stat display name
export const NETWORK_STATS = 'Network Stats';

// "Freeze in hell, Batman!"
recursiveFreeze(constants);

export default constants;
